import { ContentService, DcrKeyPipe } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BusinessComponent, BusinessComponentOptions, ImageRenderer, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { CellDataOptions, CellUtil, ComponentData, DateFormatter, TabData } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';
import { DisclosureComponent, HeadingComponent, HeadingLevel, SvgImage } from '@ddt-web/pdf-components';
import * as get from 'lodash/get';

export class CommentaryComponent extends BusinessComponent {

	@InjectConfig(ModelsExperienceConfigKey.ENABLE_COMMENTARY_KEYS_MAPPING) private readonly enableCommentaryKeysMapping: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_PDF_TRADE_RATIONALE_IMG) private readonly showDynamicPdfTradeRationaleImg: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;

	constructor(name: string, public dataComponentId: string, public visible: boolean = true, public asOfDate: string = '', public isKeyTakeaways: boolean = false) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		const tabData: TabData = componentData.getTabById('family.commentary');
		return componentData && componentData.success && tabData && !tabData.error;
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		const panels: AbstractRenderer<SVGGElement>[] = [];
		const dcrKeyPipe = new DcrKeyPipe();

		if (this.isComponentDataVisible(componentData)) {
			let commentaryDcrKey: string;
			let headingKey: string;
			const cells = get(componentData.getTabById('family.commentary'), 'tableData[0].cells[0]', []);
			const familyId = get(componentData.getTabById('family.commentary'), 'tableData[0].portfolioData[0].rawValue', '');
			const commentaryCell = CellUtil.getById(cells, 'commentaryTypeRef') as CellDataOptions;

			if (this.enableCommentaryKeysMapping) {
				commentaryDcrKey = this.isKeyTakeaways ? dcrKeyPipe.transform(commentaryCell?.rawValue + '_KEY_TAKEAWAYS')
					: dcrKeyPipe.transform(commentaryCell?.rawValue + '_TRADE_RATIONALE');
				headingKey = this.isKeyTakeaways ? dcrKeyPipe.transform('PDF_COMMENTARY_KEY_TAKEAWAYS_H1') : dcrKeyPipe.transform('PDF_COMMENTARY_TRADE_RATIONALE_H1');
			} else {
				commentaryDcrKey = dcrKeyPipe.transform(commentaryCell?.rawValue);
				headingKey = dcrKeyPipe.transform('PDF_COMMENTARY_H1');
			}
			const commentaryDcrValue = contentSvc.get(commentaryDcrKey, '');
			// Waris - this is actually not working as intended, but we keep it for now, PdM prefer not to change
			// The expected behavior is to show "not available message" but line 25 broke it
			// as "not available" translated value returns from data layer and not the key so line 25 will always be false for not available
			if (commentaryDcrValue) {
				const commentaryDcrKeyPdfOverride = commentaryDcrKey + '_PDF';
				const commentaryPanel = new StaticParagraphComponent(commentaryDcrKeyPdfOverride, commentaryDcrValue, {}, { marginBottom: 8 });

				panels.push(new HeadingComponent(headingKey, 'COMMENTARY', HeadingLevel.H1));
				panels.push(
					new StaticParagraphComponent(
						'PDF_COMMENTARY_AS_OF',
						'As of {0}',
						{ 0: new DateFormatter(this.dateFormat).format(this.asOfDate) },
						{ paddingBottom: 10 }
					)
				);
				panels.push(commentaryPanel);
				if (!this.isKeyTakeaways) {
					panels.push(new DisclosureComponent('PDF_COMMENTARY_DISCLOSURE', 'PDF_COMMENTARY_DISCLOSURE', {}, { paddingTop: 15, paddingBottom: 20 }));
				}

				if (this.showDynamicPdfTradeRationaleImg) {
					panels.push(
						new RowPanel(
							'',
							[
								new ImageRenderer(contentSvc.get(`IMAGE_${familyId}`, ''))
							],
							{},
							Alignment.Horizontal.Left
						)
					);
				} else {
					panels.push(
						new RowPanel(
							'',
							[
								new ImageRenderer(new SvgImage(`IMAGE_${familyId}`).getStaticContent())
							],
							{},
							Alignment.Horizontal.Left
						)
					);
				}
				// Disclosure for family specific image
				panels.push(new DisclosureComponent(`IMAGE_${familyId}_DISCLOSURE`, '', {}, { paddingTop: 0, paddingBottom: 20 }));
			}
		}

		return panels;
	}
}
